import axios from 'axios';
import { waitForReauth } from './authSession';
import { notifyCalendarChanged } from './calendarToday';

const api = axios.create({
  baseURL: '/api',
});

// 5.21.0: a client hidden from this user (restricted) answers exactly like a missing id — 404.
// One neutral message for both, everywhere a client or file is opened.
export const NOT_AVAILABLE_MSG = 'Not found or not available to you';
export const isNotFound = (e) => e?.response?.status === 404;

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// 401 = expired login (5.5.0, item f): no reload. Wait for the user to sign in
// again (ReloginBox, or any other InsureDocs window), then retry this request
// once. Cancel rejects with AuthCancelled and the screen keeps its state.
// Background polls (config.background) and the sign-in call itself fail silently.
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config || {};
    if (error.response?.status === 401 && !config._authRetried && !config.background
        && !String(config.url || '').includes('/auth/token')) {
      const used = String(config.headers?.Authorization || '').replace(/^Bearer /, '');
      await waitForReauth(used);
      config._authRetried = true;
      return api(config);                 // request interceptor sets the new token
    }
    return Promise.reject(error);
  }
);

export const auth = {
  login: (username, password) => {
    const form = new FormData();
    form.append('username', username);
    form.append('password', password);
    return api.post('/auth/token', form);
  },
  me: () => api.get('/auth/me'),
  listVisibleUsers: () => api.get('/auth/users/visible'),
};

export const clients = {
  list: (skip = 0, limit = 200) => api.get(`/clients/?skip=${skip}&limit=${limit}`),
  byCabinet: () => api.get('/clients/by-cabinet'),
  createNew: (data) => api.post('/clients/create-new', data),
  nextNumber: () => api.get('/clients/next-number'),
  // 5.14.0: warn-only duplicate check before + New Client creates
  possibleDuplicates: ({ name = '', phone = '', email = '' }) =>
    api.get('/clients/possible-duplicates', { params: { name, phone, email } }),
  setFlag: (id, data) => api.post(`/clients/${id}/flag`, data),
  createCabinet: (data) => api.post('/clients/create-cabinet', data),
  deleteCabinet: (id) => api.delete(`/clients/delete-cabinet/${id}`),
  get: (id) => api.get(`/clients/${id}`),
  search: (q, mode = 'all') => api.get(`/clients/search?q=${encodeURIComponent(q)}&mode=${mode}`),
  create: (data) => api.post('/clients/', data),
  update: (id, data) => api.put(`/clients/${id}`, data),
  getFolders: (id) => api.get(`/clients/${id}/folders`),
  createFolder: (id, data) => api.post(`/clients/${id}/folders`, data),
  getAudit: (id) => api.get(`/clients/${id}/audit`),
  // Manager/admin-only note log (backend require_role-gated; these are only
  // ever called when the UI has already confirmed an admin/manager viewer).
  managerNotes: {
    list: (id) => api.get(`/clients/${id}/manager-notes`),
    add: (id, note) => api.post(`/clients/${id}/manager-notes`, { note }),
    remove: (id, noteId) => api.delete(`/clients/${id}/manager-notes/${noteId}`),
  },
};

export const files = {
  list: (clientId, folderId) => {
    let url = `/files/client/${clientId}`;
    if (folderId) url += `?folder_id=${folderId}`;
    return api.get(url);
  },
  upload: (clientId, folderId, file, onProgress) => {
    const form = new FormData();
    form.append('file', file);
    return api.post(`/files/upload/${clientId}/${folderId}`, form, {
      onUploadProgress: (e) => {
        if (onProgress) onProgress(Math.round((e.loaded * 100) / e.total));
      },
    });
  },
  updateMetadata: (id, data) => api.put(`/files/metadata/${id}`, data),
  moveFolder: (id, targetFolderId) => api.post(`/files/move-folder/${id}`, { target_folder_id: targetFolderId }),
  delete: (id) => api.delete(`/files/${id}`),
  downloadUrl: (id) => `/api/files/download/${id}`,
  uploadV2: (clientId, folderId, file, sha256, onProgress) => {
    const form = new FormData();
    form.append('file', file);
    form.append('sha256', sha256);
    return api.post(`/files/upload_v2/${clientId}/${folderId}`, form, {
      onUploadProgress: (e) => { if (onProgress) onProgress(e); },
    });
  },
  // retried (v5.10.2): true when the delete succeeded on the engine's one retry → "success (after retry)".
  logClientDelete: (fileId, filename, success, error, retried) =>
    api.post('/files/audit/client-delete', { file_id: fileId, filename, success, error: error || null, retried: !!retried }),

  viewUrl: (id) => `/api/files/view/${id}`,
};

export const search = {
  global: (q, mode = 'all') => api.get(`/search/?q=${encodeURIComponent(q)}&mode=${mode}`),
};

export const audit = {
  list: (params) => api.get('/audit/', { params }),
};


// ===========================================================================
// Bonuses (v3.0)
// ===========================================================================

export const bonuses = {
  // Staff
  create: (data) => api.post('/bonuses', data),
  listMine: (params) => api.get('/bonuses/mine', { params }),
  byClient: (clientId) => api.get(`/bonuses/by-client/${clientId}`),
  get: (id) => api.get(`/bonuses/${id}`),
  auditLog: (id) => api.get(`/bonuses/${id}/audit-log`),
  update: (id, data) => api.patch(`/bonuses/${id}`, data),
  finalize: (id) => api.post(`/bonuses/${id}/finalize`),
  closePeriod: () => api.post('/bonuses/close-period'),
  printSingleUrl: (id) => `/api/bonuses/${id}/print`,
  printYearlyUrl: (year) => `/api/bonuses/mine/print/yearly?year=${year}`,
  printLastPeriodUrl: () => '/api/bonuses/mine/print/last-period',
  printUnpaidUrl: () => '/api/bonuses/mine/print/unpaid',
  // v5.9.0: the current user's own one-time bonuses (non-voided, read-only)
  mineOneTime: () => api.get('/bonuses/mine/one-time'),
  // v5.49.0 my carry-overs still waiting for my next payout
  mineCarryovers: () => api.get('/bonuses/mine/carryovers'),
};

// v5.9.0: the two paying companies. Values match backend COMPANY_NAMES keys;
// labels are used ONLY as select-option text — everywhere else the display
// name comes from the API (paying_company_name).
export const PAYING_COMPANY_OPTIONS = [
  { value: 'harry_dao', label: 'Harry Dao Insurance Agency, Inc.' },
  { value: 'solutions', label: 'Solutions Insurance Agency, Inc.' },
];

export const payoutBatches = {
  listOpen: () => api.get('/payout-batches/open'),
  listArchived: (limit = 50, offset = 0) =>
    api.get('/payout-batches/archived', { params: { limit, offset } }),
  getDetail: (id) => api.get(`/payout-batches/${id}`),
  // payload: { payment_method, payment_reference, notes, paying_company } —
  // paying_company is REQUIRED since v5.9.0 (422 without it).
  markPaid: (id, payload) => api.post(`/payout-batches/${id}/mark-paid`, payload),
  returnEntryToDraft: (entryId) =>
    api.post(`/payout-batches/entries/${entryId}/return-to-draft`),
  printUrl: (id) => `/api/payout-batches/${id}/print`,
  // v4.6 batch-level adjustments (manager/admin, open batches only)
  addAdjustment: (batchId, payload) =>
    api.post(`/payout-batches/${batchId}/adjustments`, payload),
  editAdjustment: (entryId, payload) =>
    api.patch(`/payout-batches/adjustments/${entryId}`, payload),
  deleteAdjustment: (entryId) =>
    api.delete(`/payout-batches/adjustments/${entryId}`),
  // v5.45.0 split payments (admin only). markPaid also takes { parts: [{ paying_company, amount, delayed,
  // expected_date }] } instead of paying_company (method / reference then optional).
  delayedParts: () => api.get('/payout-batches/delayed-parts'),
  parts: (batchId) => api.get(`/payout-batches/${batchId}/parts`),            // + Books lock per part (503 if Books is down)
  // v5.49.0 carryoverReason: needed when a regular batch's new paid total differs from what it owes
  editSplit: (batchId, parts, carryoverReason) =>
    api.put(`/payout-batches/${batchId}/parts`, { parts, carryover_reason: carryoverReason || null }),
  markPartPaid: (batchId, partId, paidDate) =>
    api.post(`/payout-batches/${batchId}/parts/${partId}/paid`, { paid_date: paidDate || null }),
};

export const adminBonuses = {
  // Review queue (always employee-scoped)
  list: (params) => api.get('/admin/bonuses', { params }),
  // v4.15: chargeback/adjustment strip for the review period view —
  // computed independently of the ?status= filter
  periodSummary: (params) => api.get('/admin/bonuses/period-summary', { params }),
  // v5.49.0 waiting carry-overs (all, or one employee's with { employee_id })
  carryovers: (params) => api.get('/admin/bonuses/carryovers', { params }),
  // Dashboard
  dashboard: (params) => api.get('/admin/bonuses/dashboard', { params }),
  dashboardExportUrl: (year, month) =>
    `/api/admin/bonuses/dashboard/export?period_year=${year}&period_month=${month}`,
  // Audit
  auditLog: (id) => api.get(`/admin/bonuses/${id}/audit-log`),
  // Conflict drawer — Endpoint A: client history grouped by policy
  clientPolicyHistory: (clientId, highlightPolicy) =>
    api.get(`/admin/bonuses/client/${clientId}/history`, { params: { highlight_policy: highlightPolicy } }),
  // Conflict drawer — Endpoint B: conflict-relevant decision trail (entry-level)
  conflictDetail: (id) => api.get(`/admin/bonuses/${id}/conflict-detail`),
  // Edit / actions
  update: (id, data) => api.patch(`/admin/bonuses/${id}`, data),
  review: (id, data) => api.post(`/admin/bonuses/${id}/review`, data || {}),
  pay: (id, data) => api.post(`/admin/bonuses/${id}/pay`, data),
  returnToDraft: (id, data) =>
    api.post(`/admin/bonuses/${id}/return-to-draft`, data || {}),
  void: (id, data) => api.post(`/admin/bonuses/${id}/void`, data),
  overrideFlag: (id, data) =>
    api.post(`/admin/bonuses/${id}/override-flag`, data),
  // Year-end
  // v5.9.0: one 1099 statement per paying company — company is REQUIRED
  // (harry_dao | solutions); the backend returns 422 without it.
  // v5.9.0: 1099 picker — active bonus producers + anyone paid in `year`
  // (inactive included). [{ id, full_name, is_active }]
  yearly1099Producers: (year) => api.get('/admin/bonuses/yearly-1099/producers', { params: { year } }),
  employeeYearlyStatementUrl: (userId, year, company) =>
    `/api/admin/bonuses/employee/${userId}/yearly-1099?year=${year}&company=${encodeURIComponent(company)}`,
  employeeLastPeriodUrl: (id) => '/api/admin/bonuses/employee/' + id + '/last-period',
  employeeUnpaidUrl: (id) => '/api/admin/bonuses/employee/' + id + '/unpaid',
  employeeYearlyPrintUrl: (id, year) => '/api/admin/bonuses/employee/' + id + '/print/yearly?year=' + year,
  yearlyAllExportUrl: (year) =>
    `/api/admin/bonuses/yearly-1099/all/export?year=${year}`,
  // v5.9.0 Bonus companies page (admin only): which company paid each PAID batch
  batchCompanies: (params) => api.get('/admin/bonuses/batch-companies', { params }),
  setBatchCompany: (batchId, payingCompany) =>
    api.put(`/admin/bonuses/batch-companies/${batchId}`, { paying_company: payingCompany }),
  // v5.9.0 one-time bonuses (admin only). Created already paid; corrections
  // by void only (reason required) — there is no edit or delete.
  oneTime: {
    create: (data) => api.post('/admin/bonuses/one-time', data),
    list: (params) => api.get('/admin/bonuses/one-time', { params }),
    void: (id, reason) => api.post(`/admin/bonuses/one-time/${id}/void`, { reason }),
  },
};

export const renewals = {
  // Carriers
  listCarriers: (params) => api.get('/renewals/carriers', { params }),
  createCarrier: (data) => api.post('/renewals/carriers', data),
  updateCarrier: (id, data) => api.patch(`/renewals/carriers/${id}`, data),

  // Cycles - month view
  listCycles: (params) => api.get('/renewals/cycles', { params }),
  marketingPool: (params) => api.get('/renewals/cycles/marketing', { params }),
  lapsedCustomers: (params) => api.get('/renewals/cycles/lapsed', { params }),

  // Cycle actions
  updateCycleStatus: (id, data) => api.patch(`/renewals/cycles/${id}/status`, data),
  reassignCycle: (id, data) => api.patch(`/renewals/cycles/${id}/assign`, data),
  // v5.26.0: former employees (all users). reassignFormer body: { cycle_ids, to_user_id }
  formerEmployees: () => api.get('/renewals/former-employees'),
  formerEmployeesCount: () => api.get('/renewals/former-employees/count'),
  reassignFormer: (data) => api.post('/renewals/former-employees/reassign', data),
  // v5.27.0: the one renewals picker rule — { active, hidden_with_open, former }
  assignees: () => api.get('/renewals/assignees'),
  updateCyclePremium: (id, data) => api.patch(`/renewals/cycles/${id}/premium`, data),
  // v5.6.0 workspace (notes pop-out): read + notes-only save (never status).
  // updateCycleNotes body: { outcome_notes?, alternate_options?, expected_updated_at }
  getCycleWorkspace: (id) => api.get(`/renewals/cycles/${id}/workspace`),
  updateCycleNotes: (id, data) => api.patch(`/renewals/cycles/${id}/notes`, data),
  // v5.6.0: upload a renewal document (any type) — multipart file + client sha256, like files.uploadV2.
  uploadCycleFile: (id, file, sha256, onProgress) => {
    const form = new FormData();
    form.append('file', file);
    form.append('sha256', sha256);
    return api.post(`/renewals/cycles/${id}/files`, form, {
      onUploadProgress: (e) => { if (onProgress && e.total) onProgress(Math.round((e.loaded * 100) / e.total)); },
    });
  },

  // Finalization
  finalizeMonth: (params) => api.post('/renewals/cycles/finalize', null, { params }),

  // Late additions (admin)
  listLateAdditions: () => api.get('/renewals/cycles/late-additions'),
  reviewLateAddition: (id) => api.post(`/renewals/cycles/late-additions/${id}/review`),
  // v4.3: renew a cycle — atomically marks it renewed and creates the next
  // linked policy term + cycle (Option B). Replaces the confirm-dialog path.
  renewCycle: (id, data) => api.post(`/renewals/cycles/${id}/renew`, data),
  // 5.28.0: the Edit button — correction / rewritten (all users), and the admin Undo of a rewrite
  previewEdit: (id, data) => api.post(`/renewals/cycles/${id}/edit/preview`, data),
  editCycle: (id, data) => api.post(`/renewals/cycles/${id}/edit`, data),
  undoRewrite: (rewriteId) => api.post(`/renewals/rewrites/${rewriteId}/undo`),

  // Policies
  createPolicy: (data) => api.post('/renewals/policies', data),
  getPolicy: (id) => api.get(`/renewals/policies/${id}`),
  // v5.6.0: link / re-link the whole chain (replaces the retired PATCH /policies/{id} {client_id} path).
  // body: { client_id, expected_file_ids?, expected_current_client_id? } (re-link needs both expected_*)
  linkPolicyClient: (id, data) => api.put(`/renewals/policies/${id}/client`, data),
  relinkPreview: (id, clientId) => api.get(`/renewals/policies/${id}/relink-preview`, { params: { client_id: clientId } }),
  searchPolicies: (params) => api.get('/renewals/policies/search', { params }),
  policiesForClient: (clientId) => api.get(`/renewals/policies/by-client/${clientId}`),
  cyclesForPolicy: (id) => api.get(`/renewals/policies/${id}/cycles`),
};

export const signing = {
  // Send an existing file for e-signature. confirm_real_sms is intentionally NOT
  // surfaced as a routine UI control — real-number SMS is gated server-side by the
  // allowlist; the UI only ever sets send_sms.
  send: (data) => api.post('/sign/send', data),
  list: (params) => api.get('/sign/list', { params }),
  status: (id) => api.get(`/sign/status/${id}`),
  check: (id) => api.post(`/sign/${id}/check`),          // 5.40.3: asks Documenso; files through the existing handler
};

// Telecom Authentication codes, served by the InsureDocs backend straight from
// the shared telecom schema (NOT a proxy to :8002). Gated server-side by the
// same allow-list semantics as the Telecom app.
export const telecomAuth = {
  access: () => api.get('/telecom/auth-codes/access'),
  listCodes: (config) => api.get('/telecom/auth-codes', config),
  dismissCode: (id) => api.delete(`/telecom/auth-codes/${id}`),
};

// 5.41.0: live HealthSherpa / CMS verification codes (Telnyx texts) for the HealthSherpa tab's slide-in panel.
// All background (silent, no relogin prompt). Dismiss is browser-local — there is no delete call.
export const hsAuthCodes = {
  recent: () => api.get('/healthsherpa/auth-codes/recent', { background: true }),
  viewed: (id) => api.post(`/healthsherpa/auth-codes/${id}/viewed`, null, { background: true }),
  copied: (id) => api.post(`/healthsherpa/auth-codes/${id}/copied`, null, { background: true }),
};

export default api;

export const writer = {
  write: (data) => api.post('/writer/write', data),
  save: (data) => api.post('/writer/save', data),
  health: () => api.get('/writer/health'),
};

// 5.15.0: HealthSherpa page, phase 1 (ACA quoting). The API key never reaches the browser:
// every HealthSherpa call goes through the backend (/api/healthsherpa/*).
export const healthsherpa = {
  status: () => api.get('/healthsherpa/status'),
  ping: () => api.post('/healthsherpa/ping'),
  summary: () => api.get('/healthsherpa/summary'),
  getSettings: () => api.get('/healthsherpa/settings'),
  putSettings: (data) => api.put('/healthsherpa/settings', data),
  // 5.27.4: the ZIP goes in the POST body, never in a URL (access logs keep URLs)
  counties: (zip) => api.post('/healthsherpa/counties', { zip }),
  issuers: (state, planYear) => api.get('/healthsherpa/issuers', { params: { state, plan_year: planYear } }),
  createQuote: (data) => api.post('/healthsherpa/quotes', data),
  rerunQuote: (id) => api.post(`/healthsherpa/quotes/${id}/rerun`),
  listQuotes: (params = {}) => api.get('/healthsherpa/quotes', { params }),
  getQuote: (id) => api.get(`/healthsherpa/quotes/${id}`),
  setSelection: (id, planIds) => api.put(`/healthsherpa/quotes/${id}/selection`, { plan_ids: planIds }),
  link: (id, clientId) => api.post(`/healthsherpa/quotes/${id}/link`, { client_id: clientId }),
  unlink: (id) => api.delete(`/healthsherpa/quotes/${id}/link`),
  savePdf: (id, data) => api.post(`/healthsherpa/quotes/${id}/pdf`, data || {}),
  // 5.16.0
  enrollmentDates: () => api.get('/healthsherpa/enrollment-dates'),
  carriers: () => api.get('/healthsherpa/carriers'),
  logos: () => api.get('/healthsherpa/logos'),
  matchLogos: (filenames, carriers) => api.post('/healthsherpa/logos/match', { filenames, carriers }),
  uploadLogo: (file, issuerIds, issuerName) => {
    const form = new FormData();
    form.append('file', file);
    form.append('issuer_ids', issuerIds.join(','));
    form.append('issuer_name', issuerName || '');
    return api.post('/healthsherpa/logos', form);
  },
  removeLogo: (issuerId) => api.delete(`/healthsherpa/logos/${issuerId}`),
  // 5.19.0 phase 2: client policy status (/api/healthsherpa/policy/*)
  policy: {
    summary: () => api.get('/healthsherpa/policy/summary'),
    applications: (params = {}) => api.get('/healthsherpa/policy/applications', { params }),
    review: () => api.get('/healthsherpa/policy/review'),
    link: (applicationIds, clientId, source = 'manual') =>
      api.post('/healthsherpa/policy/link', { application_ids: applicationIds, client_id: clientId, source }),
    skip: (applicationIds, reason) => api.post('/healthsherpa/policy/skip', { application_ids: applicationIds, reason }),
    unlink: (applicationId) => api.post('/healthsherpa/policy/unlink', { application_id: applicationId }),
    clientCount: (clientId) => api.get(`/healthsherpa/policy/client/${clientId}/count`),
    clientCoverage: (clientId) => api.get(`/healthsherpa/policy/client/${clientId}/coverage`),
    refreshClient: (clientId) => api.post(`/healthsherpa/policy/client/${clientId}/refresh`),
    refreshAll: () => api.post('/healthsherpa/policy/refresh-all'),
    refreshPastYear: (planYear, force = false) => api.post('/healthsherpa/policy/refresh-past-year', { plan_year: planYear, force }),
    runs: () => api.get('/healthsherpa/policy/runs'),
    uploadExport: (filename, rows) => api.post('/healthsherpa/policy/exports', { filename, rows }),
    exports: () => api.get('/healthsherpa/policy/exports'),
    // 5.20.0 HealthSherpa Clients (people)
    // 5.27.4: search text + filters go in the POST body, never in a URL (access logs keep URLs)
    people: (params = {}) => api.post('/healthsherpa/policy/people/search', params),
    person: (id) => api.get(`/healthsherpa/policy/people/${id}`),
    personHistory: (id, params = {}) => api.get(`/healthsherpa/policy/people/${id}/history`, { params }),
    personSuggestions: (id) => api.get(`/healthsherpa/policy/people/${id}/assign-suggestions`),
    assignPerson: (id, clientId, source = 'manual') => api.post(`/healthsherpa/policy/people/${id}/assign`, { client_id: clientId, source }),
    unassignPerson: (id) => api.post(`/healthsherpa/policy/people/${id}/unassign`),
    refreshPerson: (id, override = false) => api.post(`/healthsherpa/policy/people/${id}/refresh`, override ? { override: true } : {}),   // 5.20.2
    clientPerson: (clientId) => api.get(`/healthsherpa/policy/client/${clientId}/person`),
  },
  // 5.30.0 claims, per-client workspace, follow-ups, finalized (/api/healthsherpa/work/*). Our database only.
  // Search text goes in a POST body, never a URL (5.27.4 rule).
  work: {
    meta: () => api.get('/healthsherpa/work/meta'),
    textSize: () => api.get('/healthsherpa/work/text-size'),                       // 5.31.0: per user
    saveTextSize: (percent) => api.put('/healthsherpa/work/text-size', { percent }),
    mine: (season) => api.get('/healthsherpa/work/mine', { params: season ? { season } : {} }),
    claim: (pid) => api.get(`/healthsherpa/work/people/${pid}/claim`),
    setOwner: (pid, userId, expectedOwnerId) => api.put(`/healthsherpa/work/people/${pid}/owner`,
      { user_id: userId ?? null, expected_owner_id: expectedOwnerId ?? null, check_expected: true }),
    workspace: (pid, season) => api.get(`/healthsherpa/work/people/${pid}/workspace`, { params: season ? { season } : {} }),
    saveNotes: (pid, season, body, expectedUpdatedAt) => api.put(`/healthsherpa/work/people/${pid}/notes`,
      { season, body, expected_updated_at: expectedUpdatedAt || null }),
    noteVersions: (pid, season) => api.get(`/healthsherpa/work/people/${pid}/notes/versions`, { params: { season } }),
    addTodo: (pid, season, text) => api.post(`/healthsherpa/work/people/${pid}/todos`, { season, text }),
    setTodoDone: (id, done) => api.patch(`/healthsherpa/work/todos/${id}`, { done }),
    deleteTodo: (id) => api.delete(`/healthsherpa/work/todos/${id}`),
    orderTodos: (pid, season, ids) => api.put(`/healthsherpa/work/people/${pid}/todos/order`, { season, ids }),
    addAlert: (pid, season, typeId, dueDate, note) => api.post(`/healthsherpa/work/people/${pid}/alerts`,
      { season, type_id: typeId, due_date: dueDate, note: note || null }),
    completeAlert: (id) => api.post(`/healthsherpa/work/alerts/${id}/complete`),
    uncompleteAlert: (id) => api.post(`/healthsherpa/work/alerts/${id}/uncomplete`),
    deleteAlert: (id) => api.delete(`/healthsherpa/work/alerts/${id}`),
    followups: (body) => api.post('/healthsherpa/work/followups/search', body),
    finalize: (pid, season) => api.post(`/healthsherpa/work/people/${pid}/finalize`, { season }),
    reopen: (pid, season) => api.post(`/healthsherpa/work/people/${pid}/reopen`, { season }),
    finalized: (body) => api.post('/healthsherpa/work/finalized/search', body),
    alertTypes: (includeRetired = false) => api.get('/healthsherpa/work/alert-types', { params: includeRetired ? { include_retired: 1 } : {} }),
    addAlertType: (name) => api.post('/healthsherpa/work/alert-types', { name }),
    editAlertType: (id, data) => api.patch(`/healthsherpa/work/alert-types/${id}`, data),
    deleteAlertType: (id) => api.delete(`/healthsherpa/work/alert-types/${id}`),
  },
  // 5.46.0 coverage (/api/healthsherpa/coverage/*): personal info (masked; values only from showPersonal / quotePrefill,
  // both logged), the CMS plan data, each client's matched plan, year tabs, compare. Client data only in bodies.
  coverage: {
    piColumns: () => api.get('/healthsherpa/coverage/pi-columns'),
    confirmPiColumns: (columns) => api.post('/healthsherpa/coverage/pi-columns', { columns }),
    piViews: (page = 1) => api.get('/healthsherpa/coverage/pi-views', { params: { page } }),
    personal: (pid, planYear) => api.get(`/healthsherpa/coverage/people/${pid}/personal-info`, { params: planYear ? { plan_year: planYear } : {} }),
    // 5.48.0: save values typed in an enrollment / quote to the member the user picked (ambiguous match)
    capturePick: (pid, body) => api.post(`/healthsherpa/coverage/people/${pid}/personal-info/capture`, body),
    showPersonal: (pid, planYear) => api.post(`/healthsherpa/coverage/people/${pid}/personal-info/show`, { plan_year: planYear || null }),
    savePersonal: (pid, body) => api.put(`/healthsherpa/coverage/people/${pid}/personal-info`, body),
    quotePrefill: (pid, effectiveDate) => api.post(`/healthsherpa/coverage/people/${pid}/quote-prefill`, { effective_date: effectiveDate }),
    years: (pid) => api.get(`/healthsherpa/coverage/people/${pid}/years`),
    year: (pid, year) => api.get(`/healthsherpa/coverage/people/${pid}/year/${year}`),
    pick: (pid, year, planId) => api.post(`/healthsherpa/coverage/people/${pid}/year/${year}/pick`, { plan_id: planId }),
    quotes: (pid) => api.get(`/healthsherpa/coverage/people/${pid}/quotes`),
    compare: (pid, body) => api.post(`/healthsherpa/coverage/people/${pid}/compare`, body),
    sbcPath: (year, planId) => `/api/healthsherpa/coverage/sbc/${year}/${encodeURIComponent(planId)}`,
    // 5.47.0 admin: upload a plan's SBC PDF by hand (multipart; plan year + id in the form body, never the URL)
    uploadSbc: (year, planId, file) => {
      const fd = new FormData();
      fd.append('plan_year', String(year)); fd.append('plan_id', planId); fd.append('file', file);
      return api.post('/healthsherpa/coverage/sbc/upload', fd);
    },
    cmsStatus: () => api.get('/healthsherpa/coverage/cms/status'),
    missing: () => api.get('/healthsherpa/coverage/admin/missing'),
  },
  // 5.47.0 renewal bonus (/api/healthsherpa/renewal-bonus/*). Search terms only in POST bodies (5.27.4 rule).
  renewalBonus: {
    person: (pid, year) => api.get(`/healthsherpa/renewal-bonus/people/${pid}/${year}`),
    add: (pid, body) => api.post(`/healthsherpa/renewal-bonus/people/${pid}/add`, body),
    members: (pid, year, ticks) => api.put(`/healthsherpa/renewal-bonus/people/${pid}/members`, { plan_year: year, ticks }),
    editLine: (id, perMember) => api.put(`/healthsherpa/renewal-bonus/lines/${id}`, { per_member: perMember }),
    removeLine: (id) => api.delete(`/healthsherpa/renewal-bonus/lines/${id}`),
    adjust: (id, body) => api.post(`/healthsherpa/renewal-bonus/lines/${id}/adjustments`, body),
    removeAdjustment: (id) => api.delete(`/healthsherpa/renewal-bonus/adjustments/${id}`),   // 5.48.1 admin
    mine: (year) => api.get(`/healthsherpa/renewal-bonus/mine/${year}`),
    amounts: () => api.get('/healthsherpa/renewal-bonus/amounts'),
    setAmounts: (amounts) => api.put('/healthsherpa/renewal-bonus/admin/amounts', { amounts }),
    search: (body) => api.post('/healthsherpa/renewal-bonus/admin/search', body),
    staff: () => api.get('/healthsherpa/renewal-bonus/admin/staff'),
    send: (staffUserId, year) => api.post('/healthsherpa/renewal-bonus/admin/send', { staff_user_id: staffUserId, plan_year: year }),
    finalize: (batchId) => api.post(`/healthsherpa/renewal-bonus/admin/payouts/${batchId}/finalize`),
    revert: (batchId) => api.post(`/healthsherpa/renewal-bonus/admin/payouts/${batchId}/revert`),
    reviewCancel: (bonusId) => api.post(`/healthsherpa/renewal-bonus/admin/cancellations/${bonusId}/review`),
  },
  // 5.39.0 Start enrollment (/api/healthsherpa/enroll/*). Client data ONLY in POST bodies (5.27.4 rule). The two
  // HealthSherpa links come back in create()'s response and live only in component state — never stored.
  enroll: {
    meta: () => api.get('/healthsherpa/enroll/meta'),
    prefill: (kind, id) => api.post('/healthsherpa/enroll/prefill', { kind, id: id ?? null }),
    create: (body) => api.post('/healthsherpa/enroll/sessions', body),
    delivery: (sessionId, kind) => api.post(`/healthsherpa/enroll/sessions/${sessionId}/delivery`, { kind }),
    text: (sessionId, phone, link) => api.post(`/healthsherpa/enroll/sessions/${sessionId}/text`, { phone, link }),
  },
  // 5.40.0 CMS consent by text (/api/healthsherpa/consent/*). Names / phones / emails only in POST bodies; ids in URLs.
  consent: {
    meta: () => api.get('/healthsherpa/consent/meta'),
    preview: (body) => api.post('/healthsherpa/consent/preview', body, { responseType: 'blob' }),
    send: (body) => api.post('/healthsherpa/consent/send', body),
    state: (body) => api.post('/healthsherpa/consent/state', body),
    get: (id) => api.get(`/healthsherpa/consent/${id}`),
    revoke: (id, body) => api.post(`/healthsherpa/consent/${id}/revoke`, body),
    check: (id) => api.post(`/healthsherpa/consent/${id}/check`),          // 5.40.2: asks Documenso (same completion)
    file: (id, kind) => api.get(`/healthsherpa/consent/${id}/file/${kind}`, { responseType: 'blob' }),
  },
  // 5.39.0 Open Enrollment worklist (/api/healthsherpa/oe/*). Filters go in a POST body.
  oe: {
    list: (filters = {}) => api.post('/healthsherpa/oe/list', filters),
    setStatus: (pid, status, season) => api.put(`/healthsherpa/oe/people/${pid}/status`, { status, season: season ?? null }),
    addNote: (pid, body, season) => api.post(`/healthsherpa/oe/people/${pid}/notes`, { body, season: season ?? null }),
    notes: (pid, season) => api.get(`/healthsherpa/oe/people/${pid}/notes`, { params: season ? { season } : {} }),
  },
};

// 5.24.0 FAQ tab (FaqApp.jsx). Ask is a stream — see faqStream.js (authFetch), not axios.
export const faq = {
  companies: (includeInactive = false) => api.get('/faq/companies', { params: includeInactive ? { include_inactive: true } : {} }),
  createCompany: (name) => api.post('/faq/companies', { name }),
  patchCompany: (id, data) => api.patch(`/faq/companies/${id}`, data),
  documents: (companyId, includeDeleted = false) => api.get(`/faq/companies/${companyId}/documents`, { params: includeDeleted ? { include_deleted: true } : {} }),
  upload: (companyId, file, docDate, title) => {
    const f = new FormData();
    f.append('file', file);
    if (docDate) f.append('doc_date', docDate);
    if (title) f.append('title', title);
    return api.post(`/faq/companies/${companyId}/documents`, f);
  },
  patchDocument: (id, data) => api.patch(`/faq/documents/${id}`, data),
  deleteDocument: (id) => api.delete(`/faq/documents/${id}`),
  reextract: (id) => api.post(`/faq/documents/${id}/reextract`),
  file: (id) => api.get(`/faq/documents/${id}/file`, { responseType: 'blob' }),
  page: (id, n) => api.get(`/faq/documents/${id}/pages/${n}`),
  facts: (companyId) => api.get(`/faq/companies/${companyId}/facts`),
  addFact: (companyId, text) => api.post(`/faq/companies/${companyId}/facts`, { text }),
  editFact: (id, text) => api.patch(`/faq/facts/${id}`, { text }),
  deleteFact: (id) => api.delete(`/faq/facts/${id}`),
  questions: (params = {}) => api.get('/faq/questions', { params }),
  conflicts: (params = {}) => api.get('/faq/conflicts', { params }),
  conflictCount: () => api.get('/faq/conflicts/count', { background: true }),
  resolveConflict: (id, data) => api.post(`/faq/conflicts/${id}/resolve`, data),
};

// 5.32.0 Calendar + Tasks (/api/calendar/*) on HDCal. `user` = an admin viewing another person (omit = yourself).
// Times are Chicago wall strings (calendarTime.js). Reminder polls are background (no relogin prompt).
const calUser = (user) => (user ? { user } : {});
// 5.34.0: every calendar write tells the Today panel (this window and the others) to refresh at once
const calChanged = (p) => p.then((r) => { notifyCalendarChanged(); return r; });
// 5.44.0 "HealthSherpa in use" lock (one HealthSherpa login at a time) — see hsLock.js
export const hsLockApi = {
  status: () => api.get('/hs-lock/status', { background: true }),
  claim: () => api.post('/hs-lock/claim'),
  extend: (minutes) => api.post('/hs-lock/extend', { minutes }),
  done: () => api.post('/hs-lock/done'),
  override: () => api.post('/hs-lock/override', { confirmed: true }),
  log: (limit = 300) => api.get('/hs-lock/log', { params: { limit } }),
};

export const calendarApi = {
  overview: (user) => api.get('/calendar/overview', { params: calUser(user) }),
  // 5.33.0: calendarId = one side-by-side pane (a calendar of that same view)
  events: (start, end, user, calendarId) => api.get('/calendar/events', { params: { start, end, ...calUser(user), ...(calendarId ? { calendar_id: calendarId } : {}) } }),
  panes: () => api.get('/calendar/panes'),
  savePrefs: (prefs) => api.put('/calendar/view-prefs', prefs),
  object: (id, user) => api.get(`/calendar/objects/${id}`, { params: calUser(user) }),
  createEvent: (data, user) => calChanged(api.post('/calendar/events', data, { params: calUser(user) })),
  update: (id, etag, changes, user) => calChanged(api.patch(`/calendar/objects/${id}`, { etag, changes }, { params: calUser(user) })),
  remove: (id, etag, user) => calChanged(api.delete(`/calendar/objects/${id}`, { params: { etag, ...calUser(user) } })),
  copy: (id, to, user) => calChanged(api.post(`/calendar/objects/${id}/copy`, { to }, { params: calUser(user) })),
  tasks: (user) => api.get('/calendar/tasks', { params: calUser(user) }),
  createTask: (data, user) => calChanged(api.post('/calendar/tasks', data, { params: calUser(user) })),
  reminders: () => api.get('/calendar/reminders', { background: true }),
  dismiss: (key, label) => api.post('/calendar/reminders/dismiss', { key, label }),
  refresh: (user) => calChanged(api.post('/calendar/refresh', null, { params: calUser(user) })),
  // 5.34.0 the red Today panel (always the caller's own items; background = no relogin prompt)
  today: () => api.get('/calendar/today', { background: true }),
  todayState: (state, day) => api.put('/calendar/today/state', { state, day }, { background: true }),
  // 5.38.0 client follow-ups (Personal / Tasks only; the server enforces who may target whom)
  followupTargets: () => api.get('/calendar/followups/targets', { background: true }),
  followupDefaults: (clientId) => api.get('/calendar/followups/defaults', { params: { client_id: clientId } }),
  followups: (clientId) => api.get('/calendar/followups', { params: { client_id: clientId } }),
  createFollowup: (data) => calChanged(api.post('/calendar/followups', data)),
  // 5.43.0 .ics import (the file goes with each step; nothing is kept on the server between them)
  importTargets: () => api.get('/calendar/import/targets'),
  importPreview: (file, calendarId) => {
    const fd = new FormData(); fd.append('file', file); fd.append('calendar_id', String(calendarId));
    return api.post('/calendar/import/preview', fd);
  },
  importRun: (file, calendarId, uids) => {
    const fd = new FormData(); fd.append('file', file); fd.append('calendar_id', String(calendarId)); fd.append('uids', JSON.stringify(uids));
    return calChanged(api.post('/calendar/import', fd));
  },
};
